import { History } from "lucide-react"
import { Button } from "../ui/button"
import { Card, CardContent, CardHeader } from "../ui/card"
import { Skeleton } from "../ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table"
import { Tabs, TabsList, TabsTrigger } from "../ui/tabs"

// Skeleton pour une ligne de tableau
const HistoricTableRowSkeleton = () => (
  <TableRow>
    <TableCell>
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-4 rounded" />
        <Skeleton className="h-5 w-24 rounded-full" />
      </div>
    </TableCell>
    <TableCell>
      <Skeleton className="h-4 w-32" />
    </TableCell>
    <TableCell>
      <Skeleton className="h-4 w-20" />
    </TableCell>
    <TableCell>
      <Skeleton className="h-4 w-16" />
    </TableCell>
  </TableRow>
)

// Skeleton pour le tableau complet
const HistoricTableSkeleton = () => (
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead>Action</TableHead>
        <TableHead>Auteur</TableHead>
        <TableHead>Date</TableHead>
        <TableHead>Heure</TableHead>
      </TableRow>
    </TableHeader>
    <TableBody>
      {Array.from({ length: 10 }).map((_, index) => (
        <HistoricTableRowSkeleton key={index} />
      ))}
    </TableBody>
  </Table>
)

export const HistoricLoading = () => {
  return (
    <div className="h-full flex flex-col space-y-6">
      {/* Header skeleton */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center space-x-3">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="space-y-2">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-4 w-56" />
          </div>
        </div>
        <Skeleton className="h-9 w-24" />
      </div>

      {/* Main card skeleton */}
      <Card className="flex-1 flex flex-col">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-4 w-80" />
            </div>
            <Skeleton className="h-9 w-20" />
          </div>
        </CardHeader>

        <CardContent className="flex-1 overflow-auto">
          <Tabs defaultValue="boutique" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="boutique" className="flex items-center">
                <Skeleton className="h-4 w-4 mr-2 rounded" />
                Boutique
              </TabsTrigger>
              <TabsTrigger value="prestataires" className="flex items-center">
                <Skeleton className="h-4 w-4 mr-2 rounded" />
                Prestataires
              </TabsTrigger>
            </TabsList>

            <div className="space-y-4 mt-6">
              <HistoricTableSkeleton />
            </div>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}

type HistoricErrorProps = {
  onRetry: () => void
}

export const HistoricError = ({ onRetry }: HistoricErrorProps) => {
  return (
    <div className="h-full flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="p-4 bg-red-100 dark:bg-red-900/30 rounded-lg">
          <History className="h-12 w-12 text-red-600 dark:text-red-400 mx-auto mb-2" />
          <h2 className="text-lg font-semibold text-red-800 dark:text-red-200">
            Erreur de chargement
          </h2>
          <p className="text-red-600 dark:text-red-400">
            Impossible de charger l'historique de la boutique
          </p>
        </div>
        <Button onClick={onRetry} variant="outline">
          Réessayer
        </Button>
      </div>
    </div>
  )
}

type HistoricEmptyProps = {
  searchTerm: string
  selectedActionType: string
  onResetFilters: () => void
}

export const HistoricEmpty = ({
  searchTerm,
  selectedActionType,
  onResetFilters,
}: HistoricEmptyProps) => {
  return (
    <div className="flex-1 flex items-center justify-center">
      <div className="text-center space-y-4">
        <History className="h-12 w-12 text-gray-400 mx-auto" />
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
            Aucun historique trouvé
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchTerm || selectedActionType !== "all"
              ? "Aucun résultat ne correspond à vos filtres"
              : "Aucune action n'a encore été effectuée"}
          </p>
        </div>
        {(searchTerm || selectedActionType !== "all") && (
          <Button variant="outline" onClick={onResetFilters}>
            Réinitialiser les filtres
          </Button>
        )}
      </div>
    </div>
  )
}
